import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import debounce from 'lodash/debounce';
import inventoryItemService from '../../services/inventoryItemService';
import type { InventoryItemWithBalance } from '../../services/inventoryItemService';
import menuItemFormatService from '../../services/menuItemFormatService';

/**
 * Phase 7.A.2c — actions for an UNLINKED serving option inside
 * FormatExecutionBlock. Lets the operator either link an existing InventoryItem
 * or create a new RECIPE_CONSUMPTION execution item (auto-linked + recipe editor
 * opened). No unlink/relink, no DIRECT_STOCK creation, no FE conversion math.
 */

interface FormatLinkPanelProps {
  venueId: string;
  /** MenuItem id (path segment for the format service). */
  itemId: string;
  /** MenuItemFormat row id (the serving option). */
  formatId: string;
  /** Menu item kind — 'DISH' frames inventory linking as optional (food is
   *  often not stock-tracked), so the unlinked state doesn't look unfinished. */
  itemKind?: string;
  /** Pre-filled name for a created execution item, e.g. "Negroni — Glass". */
  suggestedName?: string;
  /** Called after a successful link/create with the linked item; autoOpenRecipe
   *  is true only for a freshly-created RECIPE_CONSUMPTION item. */
  onLinked: (item: InventoryItemWithBalance, autoOpenRecipe: boolean) => void;
}

const inventoryItemLabel = (item: InventoryItemWithBalance) => {
  const name = item.displayName;
  const size = item.sizeValue != null ? `${item.sizeValue} ${item.sizeUnit ?? 'ml'}` : null;
  return size ? `${name} · ${size}` : name;
};

function extractLinkError(err: any): string {
  const status = err?.response?.status;
  const code = err?.response?.data?.error?.code;
  if (status === 409 || code === 'INVENTORY_ITEM_DUPLICATE') {
    return "An item with this name already exists — use 'Link existing item' instead.";
  }
  if (status === 403) {
    return 'You do not have permission to create or link inventory items.';
  }
  const msg = err?.response?.data?.error?.message;
  if (typeof msg === 'string' && msg) return msg;
  return 'Could not complete the action. Please try again.';
}

export default function FormatLinkPanel({
  venueId,
  itemId,
  formatId,
  itemKind,
  suggestedName,
  onLinked,
}: FormatLinkPanelProps) {
  const [mode, setMode] = useState<'idle' | 'link' | 'create'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Link-existing state
  const [searchResults, setSearchResults] = useState<InventoryItemWithBalance[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Create-recipe-item state
  const [name, setName] = useState('');

  useEffect(() => {
    if (mode === 'create') setName(suggestedName ?? '');
  }, [mode, suggestedName]);

  const runSearch = useMemo(
    () =>
      debounce(async (q: string) => {
        if (!q.trim()) {
          setSearchResults([]);
          setSearchLoading(false);
          return;
        }
        setSearchLoading(true);
        try {
          // Recipe items are always returned by the list endpoint — there is no
          // include flag to set. The old query parameter was never read by the
          // backend and was removed in RECIPE-TYPE.R7.
          const res = await inventoryItemService.getItems(
            venueId, 1, 20, 'displayName', 'asc', q,
          );
          setSearchResults(res.data ?? []);
        } catch {
          setSearchResults([]);
        } finally {
          setSearchLoading(false);
        }
      }, 350),
    [venueId],
  );
  useEffect(() => () => runSearch.cancel(), [runSearch]);

  const linkExisting = async (item: InventoryItemWithBalance | null) => {
    if (!item) return;
    setError(null);
    setBusy(true);
    try {
      await menuItemFormatService.updateItemFormat(venueId, itemId, formatId, { inventoryItemId: item._id });
      onLinked(item, false);
    } catch (err) {
      setError(extractLinkError(err));
    } finally {
      setBusy(false);
    }
  };

  const createAndLink = async () => {
    const displayName = name.trim();
    if (!displayName) {
      setError('Enter a name.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const created = await inventoryItemService.createItem(venueId, {
        // RECIPE-TYPE.R7 — recipe-execution items are now stored as type RECIPE
        // (was PURCHASED). executionBehavior is unchanged and still drives depletion.
        type: 'RECIPE',
        displayName,
        executionBehavior: 'RECIPE_CONSUMPTION',
      });
      await menuItemFormatService.updateItemFormat(venueId, itemId, formatId, { inventoryItemId: created._id });
      onLinked(created as InventoryItemWithBalance, true);
    } catch (err) {
      setError(extractLinkError(err));
    } finally {
      setBusy(false);
    }
  };

  // Food and "other" items are often not 1:1 stock-tracked, so for those the
  // unlinked state is framed as optional rather than unfinished. Beer, spirit
  // and retail are stock-bearing, so they keep the standard wording.
  const unlinkedCopy = (itemKind === 'DISH' || itemKind === 'OTHER')
    ? 'Optional — link stock only if you track this item in inventory.'
    : 'This serving option is not linked to inventory yet.';

  return (
    <Box>
      <Typography variant="body2" color="text.secondary">
        {unlinkedCopy}
      </Typography>

      {error && (
        <Alert severity="error" variant="outlined" sx={{ mt: 1, mb: 0.5 }}>
          {error}
        </Alert>
      )}

      {mode === 'idle' && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
          <Button size="small" variant="outlined" onClick={() => { setError(null); setMode('link'); }} sx={{ textTransform: 'none' }}>
            Link existing item
          </Button>
          <Button size="small" variant="outlined" onClick={() => { setError(null); setMode('create'); }} sx={{ textTransform: 'none' }}>
            Set up a recipe
          </Button>
        </Stack>
      )}

      {mode === 'link' && (
        <Box sx={{ mt: 1 }}>
          <Autocomplete
            size="small"
            options={searchResults}
            loading={searchLoading}
            disabled={busy}
            filterOptions={(x) => x}
            getOptionLabel={inventoryItemLabel}
            isOptionEqualToValue={(opt, val) => opt._id === val._id}
            onInputChange={(_, value, reason) => {
              if (reason === 'input') runSearch(value);
            }}
            onChange={(_, val) => linkExisting(val)}
            renderOption={(props, opt) => (
              <Box component="li" {...props} key={opt._id}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>{inventoryItemLabel(opt)}</Typography>
                  <Chip
                    size="small"
                    label={opt.executionBehavior === 'RECIPE_CONSUMPTION' ? 'Made from a recipe' : 'Sold from stock'}
                    color={opt.executionBehavior === 'RECIPE_CONSUMPTION' ? 'secondary' : 'info'}
                    variant="outlined"
                  />
                </Stack>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Link an existing item"
                placeholder="Search inventory…"
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searchLoading && <CircularProgress size={14} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />
          <Button size="small" onClick={() => setMode('idle')} disabled={busy} sx={{ textTransform: 'none', mt: 1 }}>
            Cancel
          </Button>
        </Box>
      )}

      {mode === 'create' && (
        <Box sx={{ mt: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField
              size="small"
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={busy}
              sx={{ flex: 1, minWidth: 240 }}
            />
            <Chip size="small" label="Made from a recipe" color="secondary" variant="outlined" />
          </Stack>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button size="small" onClick={() => setMode('idle')} disabled={busy} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={createAndLink} disabled={busy} sx={{ textTransform: 'none' }}>
              {busy ? 'Creating…' : 'Create & add recipe'}
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
